import dayjs from 'dayjs';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useUpdateInfo } from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Buttons/Button';
import { CustomCalendar } from 'src/components/CustomCalendar';
import { ModalWrapper } from 'src/components/ModalWrapper';
import { useModalContext } from 'src/hooks/useModalContext';

export const ProfileRequirementsModal = () => {
  const { t } = useTranslation();
  const [date, setDate] = useState<string>('');
  const [isError, setError] = useState<{ isRequired: string; isMustBeOldest: string }>({
    isRequired: '',
    isMustBeOldest: ''
  });

  const { closeModal, modalProps } = useModalContext<{ navigateCallback: () => void }>();

  const { mutateAsync: updateProfileInfo, isLoading } = useUpdateInfo();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!date) {
      setError((prev) => {
        return {
          ...prev,
          isRequired: t('validation.valueRequired')
        };
      });
      return;
    }
    updateProfileInfo({ birthday: dayjs(date).format('YYYY-MM-DD') })
      .then(() => {
        if (modalProps?.navigateCallback) {
          modalProps?.navigateCallback();
        }
        closeModal();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    const inputDate = event.target.value;
    const enteredDate = dayjs(inputDate, 'YYYY-MM-DD').add(18, 'years');
    if (inputDate && enteredDate.valueOf() > dayjs().valueOf()) {
      setError((prev) => {
        return {
          ...prev,
          isMustBeOldest: t('apiErrors.mustBeOldest')
        };
      });
    } else {
      setError(() => {
        return {
          isRequired: '',
          isMustBeOldest: ''
        };
      });
    }
    setDate(inputDate);
  };

  const error = isError.isMustBeOldest || isError.isRequired;

  return (
    <ModalWrapper className="landscape:max-w-[360px] 768:landscape:max-w-[340px] max-w-[340px]">
      <form
        onSubmit={handleSubmit}
        className="px-4 py-10 flex flex-col items-center justify-center">
        <p className="uppercase text-center text-xl font-bold leading-[110%] pb-7 text-dobm_title">
          {t('profileRequirements.title')}
        </p>
        <p className="text-dobm_text text-base font-medium leading-5 pb-3">
          {t('profileRequirements.text')}
        </p>
        <div className="flex flex-col w-full z-50 gap-2 pb-7">
          <CustomCalendar
            labelClassName="text-i_placeholder"
            value={date}
            onChange={handleChangeDate}
            isMonthsShort={true}
            disabled={isLoading}
            label={`${t('profileRequirements.dateOfBirth')}`}
            placeholderDay={`${t('general.dd')}`}
            placeholderMonth={`${t('general.mm')}`}
            placeholderYear={`${t('general.yyyy')}`}
          />
          {Boolean(error) && <p className="text-xs text-i_text_error mt-2">{t(error)}</p>}
        </div>
        <Button
          className="min-w-full"
          mode="button-primary"
          title={`${t('general.ok')}`}
          label={`${t('general.ok')}`}
          disabled={Boolean(isError.isMustBeOldest) || Boolean(isError.isRequired)}
          loading={isLoading}
        />
      </form>
    </ModalWrapper>
  );
};
